import { Box } from "@mui/material";
import { createFileRoute, Outlet, useMatch } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";

import { skuStoreSkuDetailSearchSchema } from "../../../../features/SKUStore/utils/skuStoreSkuDetailSearch";

import {
  getPrimaryProductImageUrl,
  useCatalogAssets,
  useIngestedProductWithErrorSnackbar,
  useProductReviews,
  useProductVariantWithErrorSnackbar,
} from "@/api/products";
import { SKUStoreDetailSideNav } from "@/features/SKUStore/components/DetailNav/SKUStoreDetailSideNav";
import { SKUStoreDetailTopNav } from "@/features/SKUStore/components/DetailNav/SKUStoreDetailTopNav";
import { useProductAssociationsTotalCount } from "@/features/SKUStore/utils/skuStoreAssociationsCount";
import { countProductCopyItems } from "@/features/SKUStore/utils/skuStoreCopyFields";
import { overlayVariantOnProduct } from "@/features/SKUStore/utils/skuStoreVariantOverlay";

export const Route = createFileRoute("/_auth/sku-store/$siteId/$skuId")({
  validateSearch: zodValidator(skuStoreSkuDetailSearchSchema),
  component: SkuDetailLayout,
});

function SkuDetailLayout() {
  const { siteId, skuId } = Route.useParams();
  const { collection, category, tag } = Route.useSearch();
  const isDetailPageMatch = useMatch({
    from: "/_auth/sku-store/$siteId/$skuId/",
    shouldThrow: false,
  });
  // Variant view is read-only, so it doesn't enable Publish.
  const { variantId } = Route.useSearch();
  const isDetailPage = Boolean(isDetailPageMatch) && !variantId;
  const ingestedProductQuery = useIngestedProductWithErrorSnackbar(
    siteId,
    skuId,
  );
  const product = ingestedProductQuery.data;
  const { variant } = useProductVariantWithErrorSnackbar(
    siteId,
    product?.vendor_product_id,
    variantId,
  );
  // Variant view shows the variant's image (same overlay as the details form),
  // and nothing until both requests land so the parent image never flashes.
  const headerImageUrl = getPrimaryProductImageUrl(
    variantId
      ? product && variant && overlayVariantOnProduct(product, variant)
      : product,
  );

  const reviewsQuery = useProductReviews(siteId, {
    ids: [skuId],
    limit: 50,
    sort_by: "newest",
  });
  const catalogAssetsQuery = useCatalogAssets(
    siteId,
    { product_id: skuId },
    { enabled: Boolean(siteId && skuId) },
  );

  const associationCount = useProductAssociationsTotalCount(siteId, skuId);

  const reviewCount = reviewsQuery.isSuccess
    ? (reviewsQuery.data.reviews[skuId] ?? []).length
    : undefined;
  const mediaCount = catalogAssetsQuery.isSuccess
    ? (catalogAssetsQuery.data.assets?.length ?? 0)
    : undefined;
  const copyCount = ingestedProductQuery.isSuccess
    ? countProductCopyItems(product)
    : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        alignSelf: "stretch",
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        width: "100%",
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      <SKUStoreDetailTopNav
        siteId={siteId}
        skuId={skuId}
        brandName={product?.brand}
        categoryName={product?.categories?.[0]}
        origin={{ collection, category, tag }}
        productTitle={product?.title}
        variantTitle={variantId ? (variant?.title ?? "") : undefined}
        isDetailPage={isDetailPage}
      />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <SKUStoreDetailSideNav
          siteId={siteId}
          skuId={skuId}
          productName={product?.title}
          headerImageUrl={headerImageUrl}
          variantCount={product?.variants?.length}
          associationCount={associationCount}
          reviewCount={reviewCount}
          mediaCount={mediaCount}
          copyCount={copyCount}
          skuScore={product?.sku_score}
          skuScoreStatus={ingestedProductQuery.status}
          skuUrl={product?.url}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            px: { xs: 2, sm: 6 },
            py: 0,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
